/*
 * Shared blob pipeline for Deckorate's AI image features (Sketch to Slide,
 * Photo Headshot, ...) AND for any other Deckorate feature that talks to
 * Azure Blob Storage through a DeckorateAPI-minted SAS (e.g. Slide Templates'
 * manifest/source-file GETs — see slide-templates/services/slideTemplatesApi.ts).
 * Two-step contract against DeckorateAPI's content backend:
 *   1. POST /content/blob-sas  { client_id, transaction_type, ... }
 *      -> a short-lived SAS URL (write-only for an upload, read-only for a
 *         fetch) + the blob_container/blob_path it's scoped to.
 *   2. Talk to that sas_url directly (Azure Blob Storage, not DeckorateAPI —
 *      the SAS query string is the only auth it needs, so this bypasses the
 *      Deckorate HttpClient/authorizedFetch and uses a bare fetch instead).
 * Each feature keeps its own allowed-format signature table, mime/extension
 * map, and blob-reference type; only the shared SAS mechanics live here —
 * `uploadImageBlob` for the upload (PUT) pipeline, `sasVersionHeaders` for
 * any other direct request (GET included) against a SAS URL.
 */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import { getErrorMessage, isAbortError } from "./connectorErrors";

const BLOB_SAS_PATH = "/v1/deckorate/content/blob-sas";
const BLOB_SAS_TRANSACTION_TYPE = "APITransaction";
const BLOB_PUT_HEADER_BLOB_TYPE = "x-ms-blob-type";
const BLOB_PUT_BLOB_TYPE_VALUE = "BlockBlob";
/** SAS query param naming the REST API version the token was signed for. */
const SAS_VERSION_QUERY_PARAM = "sv";
/** Header a USER-DELEGATION SAS (identifiable by its skoid/sktid/skv params,
 *  i.e. one signed with a Microsoft Entra identity rather than the account
 *  key) must carry its own `sv` value back as, on every request against it —
 *  GET or PUT alike. Without it Azure can't tell which version's rules to
 *  validate the delegation-key signature against, and rejects the request as
 *  unauthorized even though the SAS itself is valid. See sasVersionHeaders. */
const SAS_VERSION_HEADER = "x-ms-version";

export type ErrorLogger = (reason: string, message: string) => void;

export interface BlobUploadResult {
  readonly blobContainer?: string;
  readonly blobName: string;
}

/** One magic-number check: `bytes` must match at `offset` for the signature to hit. */
export interface ImageFormatSignatureCheck {
  readonly offset: number;
  readonly bytes: readonly number[];
}

/** A recognised image format's magic-number signature — one or more checks
 *  (e.g. WEBP needs both the RIFF container header and the WEBP fourCC). */
export interface ImageFormatSignature {
  readonly extension: string;
  readonly checks: readonly ImageFormatSignatureCheck[];
}

/** Telemetry reasons for each failure point in the upload pipeline — each
 *  feature supplies its own prefixed reason strings. */
export interface BlobUploadReasons {
  readonly formatSniff: string;
  readonly sasRequest: string;
  readonly sasVersionParse: string;
  readonly putEncode: string;
  readonly putNetwork: string;
  readonly putBodyRead: string;
}

export interface BlobUploadMessages {
  readonly blobSasFailed: string;
  readonly blobPutFailed: string;
}

interface BlobSasRequest {
  client_id: number;
  transaction_type: typeof BLOB_SAS_TRANSACTION_TYPE;
  file_name: string;
}

interface RawBlobSasAsset {
  sas_url?: unknown;
  blob_container?: unknown;
  blob_path?: unknown;
}

interface RawBlobSasResponse {
  assets?: unknown;
}

interface BlobSasAsset {
  readonly sasUrl: string;
  readonly blobContainer?: string;
  readonly blobPath: string;
}

function toBlobSasAsset(raw: unknown): BlobSasAsset | null {
  if (typeof raw !== "object" || raw === null) return null;
  const asset = raw as RawBlobSasAsset;
  if (typeof asset.sas_url !== "string" || !asset.sas_url) return null;
  if (typeof asset.blob_path !== "string" || !asset.blob_path) return null;
  return {
    sasUrl: asset.sas_url,
    blobPath: asset.blob_path,
    ...(typeof asset.blob_container === "string" ? { blobContainer: asset.blob_container } : {}),
  };
}

function firstBlobSasAsset(response: RawBlobSasResponse | null | undefined): BlobSasAsset | null {
  const assets = response?.assets;
  if (!Array.isArray(assets) || assets.length === 0) return null;
  return toBlobSasAsset(assets[0]);
}

export function base64ToBytes(base64: string) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i); // NOSONAR: atob() returns a binary string (0-255).
  }
  return bytes;
}

/** Sniff the actual image format from its bytes rather than trusting the caller's mimeType label. */
export function detectExtensionFromBytes(
  base64: string,
  signatures: readonly ImageFormatSignature[],
  probeBase64Length: number,
  onError: ErrorLogger,
  reason: string,
): string | null {
  try {
    const header = atob(base64.slice(0, probeBase64Length));
    const bytes = Array.from(header, (c) => c.charCodeAt(0)); // NOSONAR: atob() returns a binary string (0-255).
    const match = signatures.find((sig) =>
      sig.checks.every((check) => check.bytes.every((b, i) => bytes[check.offset + i] === b)),
    );
    return match?.extension ?? null;
  } catch (error) {
    // Malformed base64 falls back to the mimeType-derived extension — not fatal.
    onError(reason, getErrorMessage(error, "Signature sniff failed"));
    return null;
  }
}

async function requestBlobSasAsset(
  http: HttpClient,
  clientId: number,
  fileName: string,
  options: HttpRequestOptions,
  messages: BlobUploadMessages,
  onError: ErrorLogger,
  reasons: BlobUploadReasons,
): Promise<BlobSasAsset> {
  const request: BlobSasRequest = {
    client_id: clientId,
    transaction_type: BLOB_SAS_TRANSACTION_TYPE,
    file_name: fileName,
  };
  let response: RawBlobSasResponse | null;
  try {
    response = await http.post<RawBlobSasResponse | null>(BLOB_SAS_PATH, request, options);
  } catch (error) {
    if (!isAbortError(error)) {
      onError(reasons.sasRequest, getErrorMessage(error, "blob-sas request failed"));
    }
    throw error;
  }
  const asset = firstBlobSasAsset(response);
  if (!asset) throw new Error(messages.blobSasFailed);
  return asset;
}

/** Reads the API version a SAS was signed for. Exported for direct GET
 *  requests against a SAS URL (e.g. Slide Templates' manifest/source-file
 *  downloads) — `sasVersionHeaders` below is the header-building convenience
 *  most callers want; this is the lower-level piece for anything that needs
 *  the raw version string itself. */
export function sasApiVersion(sasUrl: string, onError: ErrorLogger, reason: string): string | null {
  try {
    return new URL(sasUrl).searchParams.get(SAS_VERSION_QUERY_PARAM);
  } catch (error) {
    onError(reason, getErrorMessage(error, "SAS URL parse failed"));
    return null;
  }
}

/**
 * Headers a direct request against a SAS URL needs to be accepted (see
 * SAS_VERSION_HEADER's doc for why). Returns an empty object when the URL
 * carries no `sv` (not a user-delegation SAS, or unparseable), so spreading
 * it into a `headers:` object is always safe.
 *
 * NOTE: adding this header makes the request non-simple, so browsers send a
 * CORS preflight. The storage account's CORS rules must therefore allow
 * `x-ms-version` on the method being used.
 */
export function sasVersionHeaders(sasUrl: string, onError: ErrorLogger, reason: string): Record<string, string> {
  const apiVersion = sasApiVersion(sasUrl, onError, reason);
  return apiVersion ? { [SAS_VERSION_HEADER]: apiVersion } : {};
}

/**
 * Rebuild a fetchable file URL from a directory-scoped SAS URL — the token
 * isn't bound to one filename, so any sibling blob under the same folder is
 * fetchable with the same query string. Shared by every Deckorate feature
 * that mints a directory SAS (`sr=d`) rather than a single-file one: Slide
 * Templates and Presentation Templates both request one manifest-folder SAS
 * and then swap in whichever sibling filename (manifest, `.cache`, source
 * `.pptx`) they actually need — see slide-templates/services/
 * slideTemplatesApi.ts and presentation-templates/services/
 * presentationTemplatesApi.ts.
 *
 * `blobPath` may be a bare filename or a full container-relative path; only
 * its final segment is used since the directory URL's path already mirrors
 * everything before it.
 */
export function directorySasFileUrl(directorySasUrl: string, blobPath: string, onParseError: (message: string) => void): string {
  const segments = blobPath.split("/");
  let fileName: string | undefined;
  for (let i = segments.length - 1; i >= 0; i--) {
    if (segments[i]) {
      fileName = segments[i];
      break;
    }
  }
  if (!fileName) return directorySasUrl;

  try {
    const url = new URL(directorySasUrl);
    let pathEnd = url.pathname.length;
    while (pathEnd > 0 && url.pathname[pathEnd - 1] === "/") pathEnd--;
    url.pathname = `${url.pathname.slice(0, pathEnd)}/${encodeURIComponent(fileName)}`;
    return url.toString();
  } catch (error) {
    onParseError((error as TypeError).message);
    return directorySasUrl;
  }
}

async function putBlob(
  sasUrl: string,
  base64: string,
  mimeType: string,
  signal: AbortSignal | undefined,
  messages: BlobUploadMessages,
  onError: ErrorLogger,
  reasons: BlobUploadReasons,
): Promise<void> {
  const versionHeaders = sasVersionHeaders(sasUrl, onError, reasons.sasVersionParse);

  let body: ReturnType<typeof base64ToBytes>;
  try {
    body = base64ToBytes(base64);
  } catch (error) {
    onError(reasons.putEncode, getErrorMessage(error, "Failed to decode image bytes for upload"));
    throw new Error(`${messages.blobPutFailed} (malformed image data)`);
  }

  let res: Response;
  try {
    res = await fetch(sasUrl, {
      method: "PUT",
      headers: {
        [BLOB_PUT_HEADER_BLOB_TYPE]: BLOB_PUT_BLOB_TYPE_VALUE,
        "Content-Type": mimeType,
        ...versionHeaders,
      },
      body,
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    // A rejected fetch() (network down, DNS failure, CORS block) is a different
    // failure mode than a well-formed-but-unsuccessful HTTP response (handled
    // below) — both need to surface as a clear, telemetry-logged error, but a
    // user-initiated cancellation isn't a failure worth logging as one.
    if (!isAbortError(error)) {
      onError(reasons.putNetwork, getErrorMessage(error, "Blob PUT network failure"));
    }
    throw error;
  }
  if (!res.ok) {
    const errText = await res.text().catch((error: unknown) => {
      onError(reasons.putBodyRead, getErrorMessage(error, "Failed to read blob PUT error body"));
      return "";
    });
    throw new Error(`${messages.blobPutFailed} (${res.status} ${errText})`);
  }
}

export interface UploadImageBlobConfig {
  readonly http: HttpClient;
  readonly clientId: number;
  readonly base64: string;
  readonly mimeType: string;
  readonly fileName: string;
  readonly options?: HttpRequestOptions;
  readonly messages: BlobUploadMessages;
  readonly onError: ErrorLogger;
  readonly reasons: BlobUploadReasons;
}

/** Upload the given image to Azure Blob Storage and return a blob reference
 *  for the feature's DeckorateAPI analysis endpoint. */
export async function uploadImageBlob(config: UploadImageBlobConfig): Promise<BlobUploadResult> {
  const options = config.options ?? {};
  const asset = await requestBlobSasAsset(
    config.http,
    config.clientId,
    config.fileName,
    options,
    config.messages,
    config.onError,
    config.reasons,
  );
  await putBlob(asset.sasUrl, config.base64, config.mimeType, options.signal, config.messages, config.onError, config.reasons);
  return { blobName: asset.blobPath, ...(asset.blobContainer ? { blobContainer: asset.blobContainer } : {}) };
}
